import React from "react";
import { useTranslation } from "react-i18next";
import { Num, WeeklyImpact as WeeklyImpactType } from "@uno/protocol";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";

type Props = {
    impact: WeeklyImpactType;
    onContinue: () => void;
    onOpenLeaderboard: () => void;
};

const PODIUM_TIERS: Record<number, string> = {
    1: "is-gold",
    2: "is-silver",
    3: "is-bronze",
};

const WeeklyImpact: React.FC<Props> = ({ impact, onContinue, onOpenLeaderboard }) => {
    const { t } = useTranslation();
    const {
        scoreBefore,
        scoreAfter,
        rankBefore,
        rankAfter,
        gamesTodayAfter,
        dailyCap,
    } = impact;

    const rankDelta = rankBefore !== null && rankAfter !== null ? rankBefore - rankAfter : 0;
    const scoreDelta = scoreAfter - scoreBefore;
    const gamesCounted = Math.min(gamesTodayAfter, dailyCap);

    return (
        <div className="ge-weekly-impact weekly-impact">
            <div className="weekly-impact-banner">
                <span>{t("weekly_impact.title")}</span>
            </div>

            <div className={`weekly-impact-shield ${rankAfter !== null ? PODIUM_TIERS[rankAfter] ?? "" : ""}`}>
                <svg viewBox="0 0 112 128" aria-hidden="true">
                    <defs>
                        <linearGradient id="weekly-impact-shield" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" />
                            <stop offset="1" />
                        </linearGradient>
                    </defs>
                    <path d="M56 4 L104 20 V62 C104 94 82 114 56 124 C30 114 8 94 8 62 V20 Z" fill="url(#weekly-impact-shield)" stroke="#fff" strokeWidth="7" strokeLinejoin="round" />
                    <path d="M56 16 L94 29 V60 C94 86 77 103 56 112" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="4" strokeLinecap="round" />
                </svg>
                <span
                    className="ge-weekly-rank"
                    data-rank-before={rankBefore ?? ""}
                    data-rank-after={rankAfter ?? ""}
                >
                    {rankAfter ?? "—"}
                </span>
            </div>

            {rankDelta !== 0 && (
                <div className={`weekly-impact-move ${rankDelta > 0 ? "is-up" : "is-down"}`}>
                    {rankDelta > 0 ? "▲" : "▼"} {t("weekly_impact.places", { count: Math.abs(rankDelta) })}
                </div>
            )}

            <div className="weekly-impact-score">
                <span
                    className="ge-weekly-score"
                    data-score-before={scoreBefore}
                    data-score-after={scoreAfter}
                >
                    {Num.compact(scoreAfter)}
                </span>
                <small>{t("room.pts")}</small>
                {scoreDelta > 0 && <em>+{Num.compact(scoreDelta)}</em>}
            </div>

            <div className="weekly-impact-daily">
                <div className="weekly-impact-daily-head">
                    <span>{t("weekly_impact.daily_games")}</span>
                    <b>{gamesCounted} / {dailyCap}</b>
                </div>
                <div className="weekly-impact-gauge">
                    {Array.from({ length: dailyCap }).map((_, i) => (
                        <div
                            key={i}
                            className={i < gamesCounted ? "ge-weekly-gauge-segment is-filled" : undefined}
                        />
                    ))}
                </div>
            </div>

            <div className="weekly-impact-actions">
                <ButtonHapticWrapper>
                    <button
                        type="button"
                        onClick={onOpenLeaderboard}
                        className="weekly-impact-secondary font-changa-one uppercase cursor-pointer"
                    >
                        {t("room.view_ranking")}
                    </button>
                </ButtonHapticWrapper>
                <ButtonHapticWrapper>
                    <button
                        type="button"
                        onClick={onContinue}
                        className="game-btn rounded-[60px] font-changa-one uppercase tracking-wide text-[#195b00] cursor-pointer"
                    >
                        <span className="relative z-10">{t("weekly_impact.continue")}</span>
                    </button>
                </ButtonHapticWrapper>
            </div>
        </div>
    );
};

export default WeeklyImpact;
